import type { MergedAmmoData, WeaponData } from "@/lib/types/weapon";
import { formatDistance } from "@/lib/utils/format-distance";
import { formatTime } from "@/lib/utils/format-time";
import { formatNumber } from "@/lib/utils/format-number";

/**
 * Field configuration for weapon/ammo fields
 * Defines display names and formatting functions for consistent field rendering
 */
export interface WeaponFieldConfig {
  displayName: string;
  formatter: (value: unknown) => string;
}

/**
 * Weapon field display configuration for ammo-level fields
 */
export const WEAPON_AMMO_FIELD_CONFIG: Record<string, WeaponFieldConfig> = {
  // Damage fields
  heDamage: {
    displayName: "HE Damage",
    formatter: (value) => formatNumber(value as number),
  },
  heDamageRadius: {
    displayName: "HE Damage Radius",
    formatter: (value) => formatDistance(value as number),
  },
  suppress: {
    displayName: "Suppress",
    formatter: (value) => formatNumber(value as number),
  },
  suppressDamagesRadius: {
    displayName: "Suppress Damage Radius",
    formatter: (value) => formatDistance(value as number),
  },
  penetration: {
    displayName: "Penetration",
    formatter: (value) => formatNumber(value as number),
  },
  damageFamily: {
    displayName: "Damage Family",
    formatter: (value) => String(value || "-"),
  },
  damageIndex: {
    displayName: "Damage Index",
    formatter: (value) => String(value || "-"),
  },
  damageDropOff: {
    displayName: "Damage Drop Off",
    formatter: (value) => formatNumber(value as number),
  },

  // Range fields
  groundMaxRange: {
    displayName: "Ground Max Range",
    formatter: (value) => formatDistance(value as number),
  },
  groundMinRange: {
    displayName: "Ground Min Range",
    formatter: (value) => formatDistance(value as number),
  },
  heliMaxRange: {
    displayName: "Heli Max Range",
    formatter: (value) => formatDistance(value as number),
  },
  heliMinRange: {
    displayName: "Heli Min Range",
    formatter: (value) => formatDistance(value as number),
  },
  planeMaxRange: {
    displayName: "Plane Max Range",
    formatter: (value) => formatDistance(value as number),
  },
  planeMinRange: {
    displayName: "Plane Min Range",
    formatter: (value) => formatDistance(value as number),
  },

  // Timing fields
  aimingTime: {
    displayName: "Aiming Time",
    formatter: (value) => formatTime(value as number),
  },
  reloadTime: {
    displayName: "Reload Time",
    formatter: (value) => formatTime(value as number),
  },
  salvoLength: {
    displayName: "Salvo Length",
    formatter: (value) => formatNumber(value as number),
  },
  timeBetweenSalvos: {
    displayName: "Time Between Shots",
    formatter: (value) => formatTime(value as number),
  },
  ammunitionPerSalvo: {
    displayName: "Ammunition Per Salvo",
    formatter: (value) => formatNumber(value as number),
  },
  rateOfFire: {
    displayName: "Rate of Fire",
    formatter: (value) => formatNumber(value as number),
  },
  trueRateOfFire: {
    displayName: "True Rate of Fire",
    formatter: (value) => formatNumber(value as number),
  },
  supplyCostPerSalvo: {
    displayName: "Supply Cost Per Salvo",
    formatter: (value) => formatNumber(value as number),
  },

  // Accuracy fields
  staticAccuracy: {
    displayName: "Static Accuracy",
    formatter: (value) => `${formatNumber(value as number)}%`,
  },
  movingAccuracy: {
    displayName: "Moving Accuracy",
    formatter: (value) => value !== undefined ? `${formatNumber(value as number)}%` : "-",
  },
  maxStaticAccuracy: {
    displayName: "Max Static Accuracy",
    formatter: (value) => value !== undefined ? `${formatNumber(value as number)}%` : "-",
  },
  maxMovingAccuracy: {
    displayName: "Max Moving Accuracy",
    formatter: (value) => value !== undefined ? `${formatNumber(value as number)}%` : "-",
  },
  staticPrecisionBonusPerShot: {
    displayName: "Static Precision Bonus Per Shot",
    formatter: (value) => value !== undefined ? `${formatNumber(value as number)}%` : "-",
  },
  movingPrecisionBonusPerShot: {
    displayName: "Moving Precision Bonus Per Shot",
    formatter: (value) => value !== undefined ? `${formatNumber(value as number)}%` : "-",
  },
  maxSuccessiveHitCount: {
    displayName: "Max Successive Hit Count",
    formatter: (value) => value !== undefined ? formatNumber(value as number) : "-",
  },
  dispersionAtMaxRange: {
    displayName: "Dispersion (Max)",
    formatter: (value) => value !== undefined ? formatDistance(value as number) : "-",
  },
  dispersionAtMinRange: {
    displayName: "Dispersion (Min)",
    formatter: (value) => value !== undefined ? formatDistance(value as number) : "-",
  },

  // Other properties
  firesLeftToRight: {
    displayName: "Fires Left to Right",
    formatter: (value) => value ? "Yes" : "No",
  },
  distanceToTarget: {
    displayName: "Distance to Target",
    formatter: (value) => value ? "Yes" : "No",
  },
  piercingWeapon: {
    displayName: "Piercing Weapon",
    formatter: (value) => value ? "Yes" : "No",
  },
  tandemCharge: {
    displayName: "Tandem Charge",
    formatter: (value) => value ? "Yes" : "No",
  },
  isKinetic: {
    displayName: "Is Kinetic",
    formatter: (value) => value ? "Yes" : "No",
  },
  numberOfSimultaneousProjectiles: {
    displayName: "Number of Simultaneous Projectiles",
    formatter: (value) => formatNumber(value as number),
  },
  noiseMalus: {
    displayName: "Noise Malus",
    formatter: (value) => formatNumber(value as number),
  },
  shotsBeforeMaxNoise: {
    displayName: "Shots Before Max Noise",
    formatter: (value) => formatNumber(value as number),
  },
};

/**
 * Weapon field display configuration for weapon-level fields
 */
export const WEAPON_WEAPON_FIELD_CONFIG: Record<string, WeaponFieldConfig> = {
  totalHeDamage: {
    displayName: "HE",
    formatter: (value) => formatNumber(value as number),
  },
  supplyCost: {
    displayName: "Supply Cost",
    formatter: (value) => formatNumber(value as number),
  },
  numberOfWeapons: {
    displayName: "Number of Weapons",
    formatter: (value) => formatNumber(value as number),
  },
  turretRotationSpeed: {
    displayName: "Turret Rotation",
    formatter: (value) =>
      `${formatNumber((value as number) * (180 / Math.PI))}°/s`,
  },
};

/**
 * Get display name for a weapon/ammo field
 */
export function getWeaponFieldDisplayName(fieldKey: string, isAmmoField: boolean = true): string {
  const config = isAmmoField 
    ? WEAPON_AMMO_FIELD_CONFIG[fieldKey]
    : WEAPON_WEAPON_FIELD_CONFIG[fieldKey];
  return config?.displayName || fieldKey;
}

/**
 * Format a weapon/ammo field value
 */
export function formatWeaponFieldValue(
  fieldKey: string, 
  value: unknown, 
  isAmmoField: boolean = true
): string {
  const config = isAmmoField 
    ? WEAPON_AMMO_FIELD_CONFIG[fieldKey]
    : WEAPON_WEAPON_FIELD_CONFIG[fieldKey];
  if (!config) {
    return String(value ?? "-");
  }
  return config.formatter(value);
}

